import { Command, Help } from 'commander';
import { readFileSync } from 'fs';
import { fileURLToPath } from 'url';
import { dirname, join, basename } from 'path';
import { setVerbose } from './utils.js';
import { PROGRAM_DESCRIPTION } from './cli-copy.js';
import { renderRootHelp } from './root-help.js';
import { newCommand } from './commands/new.js';
import { plugCommand } from './commands/plug.js';
import { statusCommand } from './commands/status.js';
import { followCommand } from './commands/follow.js';
import { watchCommand } from './commands/watch.js';
import { logsCommand } from './commands/logs.js';
import { openCommand } from './commands/open.js';
import { infoCommand } from './commands/info.js';
import { recipesCommand } from './commands/recipes.js';
import { unplugCommand } from './commands/unplug.js';
import { linkCommand } from './commands/link.js';
import { renameCommand } from './commands/rename.js';
import { visibilityCommand } from './commands/visibility.js';
import { rollbackCommand } from './commands/rollback.js';
import { agentCommand } from './commands/agent.js';
import { versionsCommand } from './commands/versions.js';
import { downloadCommand } from './commands/download.js';
import { pickupCommand, pullCommand } from './commands/pickup.js';
// BL598 Phase 2 — kubectl-noun command groups.
import { getCommand } from './commands/get/index.js';
import { createCommand } from './commands/create/index.js';
import { describeCommand } from './commands/describe/index.js';
import { deleteCommand } from './commands/delete/index.js';
import { forkCommand } from './commands/fork.js';
import { remixCommand } from './commands/remix.js';
import { sshCommand } from './commands/ssh.js';
import { envCommand } from './commands/env/index.js';
import { secretsCommand } from './commands/secrets/index.js';
import { vaultCommand } from './commands/vault/index.js';
import { registerWireCommand } from './commands/wire.js';
import { devCommand } from './commands/dev.js';
import { scaffoldCommand } from './commands/scaffold/index.js';
import { adminCommand } from './commands/admin/index.js';
import { spaceCommand } from './commands/space/index.js';
import { domainsCommand } from './commands/domains.js';
import { hooksCommand } from './commands/hooks.js';
import { callersCommand } from './commands/caller-grants/callers.js';
import { callsCommand } from './commands/caller-grants/calls.js';
import { debugCommand } from './commands/debug/index.js';
import { completionCommand } from './commands/completion.js';
import { doctorCommand } from './commands/doctor.js';
import { loginCommand } from './commands/login.js';
import { logoutCommand } from './commands/logout.js';
import { whoamiCommand } from './commands/whoami.js';
import { useCommand } from './commands/use.js';
import { feedbackCommand } from './commands/feedback.js';

const __dirname = dirname(fileURLToPath(import.meta.url));
const pkg = JSON.parse(readFileSync(join(__dirname, '../package.json'), 'utf-8'));

/**
 * Assemble the full commander command tree with no parse side-effect.
 *
 * Extracted from `index.ts` so a generator (see `scripts/gen-cli-reference.ts`)
 * can introspect the command surface without running the CLI. `index.ts` calls
 * this and then `parseAsync()`; do not add any `process.exit`/parse behavior
 * here — those stay in `index.ts`.
 */
export function buildProgram(): Command {
  const program = new Command();

  // `grid` is THE CLI verb (founder directive 2026-07-07). The old bin aliases
  // (`gridctl`, `cloudgrid`, `cg`) have been removed — `grid` is the only bin.
  // Derive the program name from the invoked binary so `--help`/usage reflects
  // the invoked name, falling back to the default `grid` when the invocation
  // path is unavailable.
  const invokedAs = basename(process.argv[1] || 'grid');

  program
    .name(invokedAs)
    // First thing a new builder sees when they type `grid`. The old copy
    // ("in development. Most commands not yet available.") undersold a CLI that
    // now ships 40+ verbs and read as a warning. §23a: confident, concrete verb.
    .description(PROGRAM_DESCRIPTION)
    .version(pkg.version)
    .option('-v, --verbose', 'Show detailed output')
    // Required for `grid dev -- npm test` (and similar) to pass flags
    // through to the wrapped child command. The `dev` command sets
    // `passThroughOptions(true)` and commander throws on import unless the
    // parent program also has positional options enabled.
    .enablePositionalOptions(true)
    // #1766 — an unknown verb must exit non-zero with a did-you-mean
    // suggestion, not print top-level help and exit 0. Commander 13 already
    // exits non-zero and defaults this to on; pinning it explicitly guards
    // against a future default flip and documents the intent.
    .showSuggestionAfterError(true);

  // Curated root help. Replace commander's flat, description-sourced command
  // dump with a grouped layout (Basic / Advanced / Auth / Meta) plus a
  // cloudgrid.yaml primer and worked examples. `formatHelp` is inherited by
  // subcommands, so we only override the ROOT program's output and delegate to
  // the default formatter for everything else — `<cmd> --help` and
  // `help <cmd>` keep commander's per-command help intact. addHelpText is
  // intentionally not used here: the curated screen carries its own
  // orientation, and this override would bypass any `after` text anyway.
  program.configureHelp({
    formatHelp: (cmd, helper) => {
      if (cmd === program) return renderRootHelp(invokedAs);
      // Fall back to commander's stock formatter for every subcommand.
      // Delegate to the prototype, NOT the (overridden) instance: commander
      // Object.assigns this `configureHelp` override onto the Help instance, so
      // `helper.formatHelp` IS this closure. Any `.command()`-registered
      // subcommand inherits the config and would self-recurse to a stack
      // overflow (e.g. `version --help`). The prototype is the stock formatter.
      return Help.prototype.formatHelp.call(helper, cmd, helper);
    },
  });

  program.hook('preAction', (_thisCommand, _actionCommand) => {
    const opts = program.opts();
    if (opts.verbose) setVerbose(true);
  });

  program
    .command('version')
    .description('Show CLI version')
    .action(() => { process.stdout.write(`${pkg.version}\n`); });

  // `grid new` is the taught front door; `init` is a hidden alias of it
  // (muscle memory preserved, not taught). Mint-at-plug (Decision 012, amended
  // 2026-07-18): `new` scaffolds locally and mints nothing — the entity is
  // created on the first substrate-touching verb (`dev` or `plug`).
  program.addCommand(newCommand);
  program.addCommand(plugCommand);
  program.addCommand(statusCommand);
  // Backlog 072-A/B — async deploy companions.
  program.addCommand(followCommand);
  program.addCommand(watchCommand, { hidden: true });
  program.addCommand(logsCommand);
  program.addCommand(openCommand);
  program.addCommand(infoCommand);
  program.addCommand(recipesCommand);
  program.addCommand(unplugCommand);
  program.addCommand(linkCommand);
  program.addCommand(renameCommand);
  // BL598 Phase 2 — kubectl-noun groups. `get` (grids/entities/spaces),
  // `create grid`, `describe` (grid/entity), and `delete` (grid/entity) replace
  // the deleted top-level `list`, `builds`, and `delete` verbs (clean break per
  // founder direction 2026-06-28 — no backward compatibility before public
  // release).
  program.addCommand(getCommand);
  program.addCommand(createCommand);
  program.addCommand(describeCommand);
  program.addCommand(deleteCommand);
  // 0.8.11 — visibility / auth-mode controls (backlog 118).
  program.addCommand(visibilityCommand);
  // M8b — mutating-ops verbs (spec v17 §13.5–§13.9).
  program.addCommand(rollbackCommand);
  program.addCommand(agentCommand);
  program.addCommand(versionsCommand);
  // BL598 Phase 1A — `pickup` and `download` are the canonical collaboration
  // verbs (the former `clone` / `pull` were removed in Phase 2's clean break).
  program.addCommand(downloadCommand);
  program.addCommand(pickupCommand);
  // `pull` is the owner-facing alias of `pickup` (mega-plan v2 §1, §6,
  // Decision A). Same flow; distinct from `download` (bytes only, no link).
  program.addCommand(pullCommand);
  program.addCommand(forkCommand);
  // Mega-plan v2 §8.1/§8.2 — `remix` is the visibility-gated "make my own copy"
  // verb: copy an app you can SEE into any grid you build in (cross-grid), minus
  // the source's secrets. Distinct from `fork` (byte-carrying, same-grid).
  program.addCommand(remixCommand);
  program.addCommand(sshCommand);
  program.addCommand(envCommand);
  program.addCommand(secretsCommand);
  // #1766 — `grid vault` (set/list/rotate) drives the per-grid vault store
  // (decision 023). Distinct from `secrets` (K8s runtime secrets): vault items
  // are org-scope secret material with grants + resolve auditing.
  program.addCommand(vaultCommand);
  registerWireCommand(program);
  program.addCommand(devCommand);
  // Scaffold commands — per decision 015 §"Scaffold commands". Adds idempotent
  // project re-scaffolding plus `service` / `rename` subcommands so users add
  // new services without hand-editing yaml + folders.
  program.addCommand(scaffoldCommand);
  // M8c — admin/space subcommand groups (spec v17 §14). Source kept through
  // M0 clean-slate but registration was lost; re-wiring here per backlog 058.
  program.addCommand(adminCommand);
  program.addCommand(spaceCommand);
  // Backlog 016 — self-serve custom domains (add / list / verify / remove).
  program.addCommand(domainsCommand);
  // BL621 Phase 3 (Decision 042 §"Mode 6") — `hooks` is a folder-rooted
  // entity-config group, top-level now (moved out of `admin`). It operates on
  // the cwd entity / platform by default.
  program.addCommand(hooksCommand);
  // Decision 051 — App Connection grant management. `callers` is the callee
  // side (who may call an app you own: list / approve / revoke), `calls` the
  // caller side (what an app of yours calls: list / withdraw).
  program.addCommand(callersCommand);
  program.addCommand(callsCommand);
  // BL621 Phase 3 (Decision 042 §"Mode 4 — Debug") — the `debug` group
  // (`debug call`, `debug ssh <entity>`) is developer tooling, hidden from the
  // default `--help`.
  program.addCommand(debugCommand, { hidden: true });
  program.addCommand(completionCommand);
  program.addCommand(doctorCommand);
  // 0.8.4 batch — auth verbs ported back from v0.7 with v0.8 identity-model
  // adjustments. Backlog 041.2 is closed once these ship.
  program.addCommand(loginCommand);
  program.addCommand(logoutCommand);
  program.addCommand(whoamiCommand);
  program.addCommand(useCommand);
  // Decision 016 — `grid feedback` posts to the same telemetry pipeline
  // as auto error/success events. Replaces the legacy v0.7 verb (backlog 048).
  program.addCommand(feedbackCommand);
  // BL621 Phase 3 (Decision 042 §"Mode 4 — Debug") — `grid call` moved into
  // the hidden `debug` group as `debug call` (registered above).
  // BL598 Phase 2 — brain / telemetry / errors are reached ONLY under
  // `grid admin <command>` now. The top-level aliases (and their deprecation
  // shim) were removed in the clean break; `grid admin hooks deliveries
  // --limit 5` is the CLAUDE.md release-verification command.

  return program;
}
